import { useState, useEffect, useRef } from "react";
import { getAdImageSignedUrlPublic } from "@/app/api/auth-client";
import { isStoragePath, normalizeAdImageStoragePath } from "@/app/utils/ad-utils";

/** In-memory cache: storage_path -> { url, expiresAt } so we don't refetch the same path every render. */
const signedUrlCache = new Map<
  string,
  { url: string; expiresAt: number }
>();
const CACHE_BUFFER_MS = 5 * 60 * 1000; // consider expired 5 min before actual expiry

function getCached(path: string): string | null {
  const entry = signedUrlCache.get(path);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt - CACHE_BUFFER_MS) {
    signedUrlCache.delete(path);
    return null;
  }
  return entry.url;
}

function setCached(path: string, url: string, expiresInSeconds: number): void {
  signedUrlCache.set(path, {
    url,
    expiresAt: Date.now() + expiresInSeconds * 1000,
  });
}

const DEFAULT_EXPIRES_SECONDS = 3600;

/**
 * Prefetch signed URLs for multiple storage paths in parallel and fill the cache.
 * Call when you have a list of ads (e.g. after getMyAds) so AdImage components render with URLs already cached.
 */
export function prefetchAdImageUrls(storagePaths: string[]): void {
  const toFetch = storagePaths
    .map((p) => (typeof p === "string" ? normalizeAdImageStoragePath(p) : ""))
    .filter((p) => p && isStoragePath(p) && !getCached(p));
  if (toFetch.length === 0) return;
  const unique = Array.from(new Set(toFetch));
  Promise.all(
    unique.map((path) =>
      getAdImageSignedUrlPublic(path)
        .then((url) => setCached(path, url, DEFAULT_EXPIRES_SECONDS))
        .catch(() => {})
    )
  );
}

/**
 * Resolves an ad image storage path (e.g. "ads/ad_123/uuid.jpg") to a signed GET URL.
 * Uses cache to avoid repeated requests for the same path.
 */
export function useAdImageUrl(storagePath: string | null | undefined): {
  url: string | null;
  loading: boolean;
  error: string | null;
} {
  const [url, setUrl] = useState<string | null>(() => {
    if (!storagePath?.trim()) return null;
    const p = normalizeAdImageStoragePath(storagePath);
    return p && isStoragePath(p) ? getCached(p) : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!storagePath?.trim()) return false;
    const p = normalizeAdImageStoragePath(storagePath);
    return !!(p && isStoragePath(p) && !getCached(p));
  });
  const [error, setError] = useState<string | null>(null);
  const prevPath = useRef<string | null>(null);

  useEffect(() => {
    const path = storagePath ? normalizeAdImageStoragePath(storagePath) : "";
    if (!path || !isStoragePath(path)) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }
    const cached = getCached(path);
    if (cached) {
      setUrl(cached);
      setLoading(false);
      setError(null);
      return;
    }
    if (prevPath.current === path) return;
    prevPath.current = path;
    setLoading(true);
    setError(null);
    getAdImageSignedUrlPublic(path)
      .then((signedUrl) => {
        setCached(path, signedUrl, DEFAULT_EXPIRES_SECONDS);
        setUrl(signedUrl);
        setError(null);
      })
      .catch((e) => {
        setUrl(null);
        setError(e?.message ?? "Failed to load image");
      })
      .finally(() => setLoading(false));
  }, [storagePath]);

  return { url, loading, error };
}
